import type {
  BoardCollectResponse,
  BoardRunConflict,
  BoardRunState,
  BoardStopResult,
  RoadState,
} from '@goodtime/shared';
import { api, ApiError } from './client';

/** The player's Royal Road on a slot, or null when it isn't on for them
 *  there (the server answers 204) or the server can't be reached. */
export async function fetchRoad(sessionId: string, themeId: string): Promise<RoadState | null> {
  try {
    const road = await api<RoadState | undefined>(
      `/sessions/${encodeURIComponent(sessionId)}/road?themeId=${encodeURIComponent(themeId)}`,
    );
    return road ?? null;
  } catch {
    return null;
  }
}

/** The open Lamp Run on a slot, or null (204). */
export async function fetchBoard(sessionId: string, themeId: string): Promise<BoardRunState | null> {
  try {
    const run = await api<BoardRunState | undefined>(
      `/sessions/${encodeURIComponent(sessionId)}/board?themeId=${encodeURIComponent(themeId)}`,
    );
    return run ?? null;
  } catch {
    return null;
  }
}

/** Reveal STOP `stop`. The key is the same on every retry of this STOP. */
export function stopBoard(sessionId: string, runId: string, stop: number): Promise<BoardStopResult> {
  return api<BoardStopResult>(`/sessions/${encodeURIComponent(sessionId)}/board/stop`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${runId}-s${stop}` },
    body: JSON.stringify({ runId, stop }),
  });
}

/** Credit a finished run. */
export function collectBoard(sessionId: string, runId: string): Promise<BoardCollectResponse> {
  return api<BoardCollectResponse>(`/sessions/${encodeURIComponent(sessionId)}/board/collect`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `${runId}-c` },
    body: JSON.stringify({ runId }),
  });
}

/** Tell the server the Settings gamble choice (Hi-Lo bonuses follow it). */
export async function setGamblePreference(playerId: string, gambleEnabled: boolean): Promise<void> {
  await api<unknown>(`/players/${encodeURIComponent(playerId)}/preferences`, {
    method: 'PUT',
    body: JSON.stringify({ gambleEnabled }),
  });
}

/** The run in a 409 (`BOARD_RUN_OPEN` or `RUN_CONFLICT`), or null. */
export function runFromConflict(err: unknown): BoardRunState | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.body) as Partial<BoardRunConflict>;
    return body.boardRun ?? null;
  } catch {
    return null;
  }
}

/** When a spin was refused for want of coins (409 `OUT_OF_COINS`), the time
 *  the free refill comes (epoch ms); otherwise null. */
export function outOfCoinsUntil(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  try {
    const body = JSON.parse(err.body) as { error?: string; nextRefillAt?: number };
    return body.error === 'OUT_OF_COINS' ? (body.nextRefillAt ?? 0) : null;
  } catch {
    return null;
  }
}

/**
 * Try a request again when the connection drops or the server hiccups
 * (no response, or a 5xx), up to `tries` times. Other errors (4xx,
 * including 409) come straight back.
 */
export async function withRetry<T>(fn: () => Promise<T>, tries = 3, waitMs = 600): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      last = err;
      const retryable = !(err instanceof ApiError) || err.status >= 500;
      if (!retryable || attempt === tries - 1) break;
      await new Promise((r) => setTimeout(r, waitMs * (attempt + 1)));
    }
  }
  throw last;
}
